import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "./styles.css";
import Container from "../../../components/Container";
import { Form, Input, Button, Table, Modal, notification } from "antd";
import { EditOutlined, DeleteOutlined, TeamOutlined } from "@ant-design/icons";
import { db } from "../../../services/firebase";
import { collection, addDoc, getDoc, doc, updateDoc } from "firebase/firestore";
import { registrarAtividade } from "../../../services/atividadeService";

const { useForm, Item } = Form;

function ClientForm() {
  const { id } = useParams();
  const [form] = useForm();
  const [formUnidade] = useForm();

  const [unidades, setUnidades] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unidadeEditing, setUnidadeEditing] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const columns = [
    {
      title: "Unidade",
      dataIndex: "unidade",
      key: "unidade",
    },
    {
      title: "CNPJ",
      dataIndex: "cnpj",
      key: "cnpj",
    },
    {
      title: "Ações",
      dataIndex: "index",
      key: "actions",
      width: 100,
      render: (index, record) => (
        <div className="actions-cell">
          <button className="action-btn edit" onClick={() => editUnidade(record)}>
            <EditOutlined />
          </button>
          <button className="action-btn delete" onClick={() => deleteUnidade(index)}>
            <DeleteOutlined />
          </button>
        </div>
      ),
    },
  ];

  const editUnidade = (unidade) => {
    setUnidadeEditing(unidade.index);
    formUnidade.setFieldsValue({
      unidade: unidade.unidade,
      cnpj: unidade.cnpj,
    });
    setIsModalOpen(true);
  };

  const deleteUnidade = (indexToDelete) => {
    Modal.confirm({
      title: "Excluir unidade?",
      content: "Tem certeza que deseja excluir esta unidade?",
      okText: "Sim, excluir",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: () => {
        const novasUnidades = unidades
          .filter((_, index) => index !== indexToDelete)
          .map((item, index) => ({ ...item, index }));
        setUnidades(novasUnidades);
        notification.success({
          message: "Unidade excluída!",
          description: "A unidade foi removida com sucesso.",
          placement: "topRight",
        });
      },
    });
  };

  // Envia os dados do formulário ao backend
  const handleSubmit = async (values) => {
    try {
      if (unidades.length === 0) {
        notification.warning({
          message: "Adicione pelo menos uma unidade",
          description: "É necessário adicionar ao menos uma unidade antes de cadastrar o cliente.",
          placement: "topRight",
        });
        return;
      }

      const data = { ...values, unidades };
      const docRef = await addDoc(collection(db, "clientes"), data);

      // Registrar atividade
      const usuario = JSON.parse(sessionStorage.getItem("seepilUsuario"));
      await registrarAtividade(
        "Novo cliente cadastrado",
        values.nomeEmpresa,
        usuario?.nomeUsuario
      );

      notification.success({
        message: "Cliente cadastrado!",
        description: `O cliente ${values.nomeEmpresa} foi cadastrado com sucesso.`,
        placement: "topRight",
      });

      // Limpa formulário
      form.resetFields();
      setUnidades([]);
    } catch (error) {
      console.error("Erro ao cadastrar cliente:", error);

      let mensagemErro = "Erro ao cadastrar cliente";
      let descricaoErro = "Não foi possível cadastrar o cliente. Verifique os dados e tente novamente.";

      if (error.code === "permission-denied") {
        mensagemErro = "Permissão negada";
        descricaoErro = "Você não tem permissão para cadastrar clientes. Entre em contato com o administrador.";
      } else if (error.code === "unavailable") {
        mensagemErro = "Serviço indisponível";
        descricaoErro = "O sistema está temporariamente indisponível. Tente novamente em alguns instantes.";
      }

      notification.error({
        message: mensagemErro,
        description: descricaoErro,
        placement: "topRight",
      });
    }
  };

  const handleSubmitEdit = async (values) => {
    try {
      if (unidades.length === 0) {
        notification.warning({
          message: "Adicione pelo menos uma unidade",
          description: "É necessário que o cliente tenha ao menos uma unidade cadastrada.",
          placement: "topRight",
        });
        return;
      }

      const data = { ...values, unidades };
      const docRef = doc(db, "clientes", id);
      await updateDoc(docRef, data);

      // Registrar atividade
      const usuario = JSON.parse(sessionStorage.getItem("seepilUsuario"));
      await registrarAtividade(
        "Cliente atualizado",
        values.nomeEmpresa,
        usuario?.nomeUsuario
      );

      notification.success({
        message: "Cliente atualizado!",
        description: `Os dados do cliente ${values.nomeEmpresa} foram atualizados com sucesso.`,
        placement: "topRight",
      });
    } catch (error) {
      console.error("Erro ao atualizar cliente:", error);

      let mensagemErro = "Erro ao atualizar cliente";
      let descricaoErro = "Não foi possível atualizar o cliente. Verifique os dados e tente novamente.";

      if (error.code === "permission-denied") {
        mensagemErro = "Permissão negada";
        descricaoErro = "Você não tem permissão para editar clientes. Entre em contato com o administrador.";
      } else if (error.code === "not-found") {
        mensagemErro = "Cliente não encontrado";
        descricaoErro = "O cliente que você está tentando editar não foi encontrado no sistema.";
      }

      notification.error({
        message: mensagemErro,
        description: descricaoErro,
        placement: "topRight",
      });
    }
  };

  const handleSubmitUnidade = (values) => {
    setUnidades([...unidades, values]);
    setIsModalOpen(false);
  };

  const handleEditUnidade = (values) => {
    const novasUnidades = unidades.map((item, index) => {
      if (index === unidadeEditing) {
        return { ...values, index };
      }
      return item;
    });
    setUnidades(novasUnidades);
    setUnidadeEditing(null);
    setIsModalOpen(false);
  };

  const getClientDetails = async () => {
    const ref = doc(db, "clientes", id);
    const snapshot = await getDoc(ref);
    const data = { id: snapshot.id, ...snapshot.data() };
    form.setFieldValue("nomeEmpresa", data?.nomeEmpresa);
    const unidadesData = data?.unidades?.map((item, index) => ({
      ...item,
      index,
    }));
    setUnidades(unidadesData);
  };

  useEffect(() => {
    if (id) {
      getClientDetails();
      return;
    }
    form.resetFields();
    formUnidade.resetFields();
    setUnidadeEditing(null);
    setUnidades([]);
  }, [id]);

  return (
    <Container>
      <div className="client-form-container">
        <div className="form-card">
          <div className="form-header">
            <div className="form-header-left">
              <div className="form-icon">
                <TeamOutlined />
              </div>
              <div>
                <h2 className="form-title">{id ? "Editar" : "Cadastrar"} Cliente</h2>
              </div>
            </div>
          </div>

          {successMessage && (
            <p className="success-message">{successMessage}</p>
          )}

          <Form
            form={form}
            onFinish={id ? handleSubmitEdit : handleSubmit}
            layout="vertical"
            className="custom-form"
          >
            <Item label="Nome da Empresa" name="nomeEmpresa">
              <Input placeholder="Digite o nome da Empresa" required />
            </Item>

            <div className="form-table-section">
              <div className="form-table-header">
                <h3 className="form-table-title">Unidades</h3>
                <Button onClick={() => setIsModalOpen(true)} className="btn-secondary">
                  Adicionar unidade
                </Button>
              </div>
              <Table
                columns={columns}
                dataSource={unidades}
                className="custom-form-table"
                pagination={false}
              />
            </div>

            <div className="form-actions">
              <Button type="primary" htmlType="submit" className="btn-primary">
                {id ? "Salvar Alterações" : "Cadastrar Cliente"}
              </Button>
            </div>
          </Form>

          <Modal
            title={unidadeEditing !== null ? "Editar Unidade" : "Cadastrar Unidade"}
            open={isModalOpen}
            footer={false}
            onClose={() => setIsModalOpen(false)}
            onCancel={() => setIsModalOpen(false)}
            className="custom-modal"
          >
            <Form
              form={formUnidade}
              onFinish={
                unidadeEditing !== null
                  ? handleEditUnidade
                  : handleSubmitUnidade
              }
              layout="vertical"
              className="custom-form"
            >
              <Item
                label="Nome da Unidade"
                name="unidade"
                rules={[
                  { required: true, message: "Nome da Unidade é obrigatório" },
                ]}
              >
                <Input placeholder="Digite o nome da Unidade" />
              </Item>

              <Item
                label="CNPJ"
                name="cnpj"
                rules={[{ required: true, message: "CNPJ é obrigatório" }]}
              >
                <Input placeholder="ex: 0000" />
              </Item>

              <Button type="primary" htmlType="submit" className="btn-primary">
                {unidadeEditing !== null ? "Salvar" : "Adicionar"} Unidade
              </Button>
            </Form>
          </Modal>
        </div>
      </div>
    </Container>
  );
}

export default ClientForm;
